import React, { useState, useEffect } from "react";
import { useAuth } from "./contexts/AuthContext";
import { listUsers, reenableUser } from "./utils/userManagementApi";
import logger from "./utils/logger";
import AddUserModal from "./AddUserModal";
import EditUserModal from "./EditUserModal";
import DeleteUserConfirmModal from "./DeleteUserConfirmModal";
import UserOrgAccessModal from "./UserOrgAccessModal";
import ReenableUserConfirmModal from "./ReenableUserConfirmModal";
import "./UserManagementModal.css";

const UserManagementModal = ({ onClose }) => {
  const { getAccessToken, user, refreshUserProfile } = useAuth();

  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // Store all users for validation
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // Filters and pagination
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const limit = 20;

  // Sub-modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [managingUserOrgs, setManagingUserOrgs] = useState(null);
  const [reenablingUser, setReenablingUser] = useState(null);
  const [isReenabling, setIsReenabling] = useState(false);

  // Role checks
  const isFullAdmin = user?.roles?.some(role => 
    (typeof role === 'string' ? role : role?.name) === "Full Admin"
  );
  // const isOrgAdmin = user?.roles?.some(role => 
  //   (typeof role === 'string' ? role : role?.name) === "Organization Admin"
  // );
  const canManageUsers = isFullAdmin; // Only Full Admin can create/edit/delete

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter, page]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;

      const result = await listUsers(params, getAccessToken);

      setUsers(result.users || []);
      setTotalPages(result.pagination?.totalPages || 1);
      setTotalUsers(result.pagination?.total || 0);
    } catch (error) {
      logger.error("Failed to load users", error);
      showMessage(`Error loading users: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      // Load all users (no filters, high limit) for email validation
      const result = await listUsers({ page: 1, limit: 1000 }, getAccessToken);
      setAllUsers(result.users || []);
    } catch (error) {
      logger.error("Failed to load all users for validation", error);
      // Don't show error to user, just log it
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page
  };

  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
    setPage(1);
  };

  const handleUserCreated = () => {
    setShowAddModal(false);
    loadUsers();
    loadAllUsers(); // Refresh validation list
    showMessage("User created successfully", "success");
  };

  const handleUserUpdated = async () => {
    const wasCurrentUser = editingUser && editingUser.userId === user.userId;
    setEditingUser(null);
    loadUsers();

    // If the updated user is the current user, refresh their profile
    if (wasCurrentUser) {
      await refreshUserProfile();
    }

    showMessage("User updated successfully", "success");
  };

  const handleUserDeleted = () => {
    setDeletingUser(null);
    loadUsers();
    loadAllUsers(); // Refresh validation list
    showMessage("User deleted successfully", "success");
  };

  const handleOrgAccessUpdated = () => {
    setManagingUserOrgs(null);
    loadUsers(); // Reload to update org counts
    showMessage("Organization access updated", "success");
  };

  const handleReenableUser = async () => {
    setIsReenabling(true);
    try {
      await reenableUser(reenablingUser.userId, getAccessToken);
      setReenablingUser(null);
      loadUsers(); // Refresh user list
      showMessage("User re-enabled successfully", "success");
    } catch (error) {
      logger.error("Failed to re-enable user:", error);
      showMessage(error.message || "Failed to re-enable user", "error");
    } finally {
      setIsReenabling(false);
    }
  };

  const getRoleBadgeClass = (role) => {
    if (role === "Full Admin") return "full-admin";
    if (role === "Organization Admin") return "org-admin";
    if (role === "Site Admin") return "site-admin";
    return "";
  };

  return (
    <div className="user-management-modal-overlay">
      <div className="user-management-modal">
        <div className="user-management-header">
          <h2>User Management</h2>
          <button className="user-management-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {message && (
          <div className={`user-management-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="user-management-controls">
          <div className="user-management-filters">
            <div className="user-management-search-wrapper">
              <input
                type="text"
                className="user-management-search"
                placeholder="Search by name or email..."
                value={search}
                onChange={handleSearchChange}
              />
              <div className="search-hint">
                Type "unusable" to see unusable users
              </div>
            </div>
            <select
              className="user-management-role-filter"
              value={roleFilter}
              onChange={handleRoleFilterChange}
            >
              <option value="">All Roles</option>
              <option value="Full Admin">Full Admin</option>
              <option value="Organization Admin">Organization Admin</option>
              <option value="Site Admin">Site Admin</option>
            </select>
          </div>
          <button
            className="add-user-btn"
            onClick={() => {
              // Load all users for email validation only when opening Add User modal
              if (allUsers.length === 0) {
                loadAllUsers();
              }
              setShowAddModal(true);
            }}
            disabled={!canManageUsers}
            title={
              !canManageUsers
                ? "Only Full Admins can create users"
                : "Add new user"
            }
          >
            + Add User
          </button>
        </div>

        {loading ? (
          <div className="user-management-loading"></div>
        ) : users.length === 0 ? (
          <div className="user-management-empty">
            No users found.{" "}
            {canManageUsers && "Click 'Add User' to create one."}
          </div>
        ) : (
          <>
            <div className="user-table-container">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.userId}
                      className={u.unusableAccount ? "unusable-user-row" : ""}
                    >
                      <td
                        style={{
                          color: u.unusableAccount ? "#dc3545" : "inherit",
                        }}
                      >
                        {u.firstName} {u.lastName}
                      </td>
                      <td
                        style={{
                          color: u.unusableAccount ? "#dc3545" : "inherit",
                        }}
                      >
                        {u.email}
                      </td>
                      <td>{u.phoneNumber || "—"}</td>
                      <td>
                        <span
                          className={`user-role-badge ${getRoleBadgeClass(
                            u.role
                          )}`}
                        >
                          {u.role}
                        </span>
                        {u.unusableAccount && (
                          <span
                            className={`unusable-badge ${
                              canManageUsers ? "clickable" : ""
                            }`}
                            onClick={
                              canManageUsers
                                ? () => setReenablingUser(u)
                                : undefined
                            }
                            title={
                              canManageUsers
                                ? "Click to re-enable this user"
                                : "This account cannot be used due to a federation configuration mismatch. Please delete this account."
                            }
                          >
                            UNUSABLE
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="user-actions">
                          <button
                            className="user-action-btn orgs"
                            onClick={() => setManagingUserOrgs(u)}
                            disabled={u.unusableAccount}
                            title={
                              u.unusableAccount
                                ? "Cannot manage organizations for unusable accounts"
                                : "Manage organizations"
                            }
                          >
                            Orgs
                          </button>
                          <button
                            className="user-action-btn edit"
                            onClick={() => setEditingUser(u)}
                            disabled={!canManageUsers}
                            title={
                              !canManageUsers
                                ? "Only Full Admins can edit users"
                                : "Edit user"
                            }
                          >
                            Edit
                          </button>
                          <button
                            className="user-action-btn delete"
                            onClick={() => setDeletingUser(u)}
                            disabled={
                              !canManageUsers || u.role === "Full Admin"
                            }
                            title={
                              u.role === "Full Admin"
                                ? "Full Admin users cannot be deleted"
                                : !canManageUsers
                                ? "Only Full Admins can delete users"
                                : "Delete user"
                            }
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="user-pagination">
              <button
                className="pagination-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {page} of {totalPages} ({totalUsers} total users)
              </span>
              <button
                className="pagination-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          </>
        )}

        <button className="close-btn" onClick={onClose}>
          Close
        </button>

        {showAddModal && (
          <AddUserModal
            onClose={() => setShowAddModal(false)}
            onUserCreated={handleUserCreated}
            existingUsers={allUsers}
          />
        )}

        {editingUser && (
          <EditUserModal
            user={editingUser}
            onClose={() => setEditingUser(null)}
            onUserUpdated={handleUserUpdated}
          />
        )}

        {deletingUser && (
          <DeleteUserConfirmModal
            user={deletingUser}
            onClose={() => setDeletingUser(null)}
            onUserDeleted={handleUserDeleted}
          />
        )}

        {managingUserOrgs && (
          <UserOrgAccessModal
            user={managingUserOrgs}
            onClose={() => setManagingUserOrgs(null)}
            onAccessUpdated={handleOrgAccessUpdated}
          />
        )}

        {reenablingUser && (
          <ReenableUserConfirmModal
            user={reenablingUser}
            onConfirm={handleReenableUser}
            onCancel={() => setReenablingUser(null)}
            isLoading={isReenabling}
          />
        )}
      </div>
    </div>
  );
};

export default UserManagementModal;
